import type { RetrievedChunk } from '@/services/Conversations';

/**
 * Collision-free identity of a chunk, or `null` when it has none.
 *
 * Namespaced by collection because `chunkId` is a point id **per collection**:
 * the same id in `support_v36` and in `support_antiexamples_v32` denotes
 * different chunks.
 *
 * Returns `null` — rather than a synthesised fallback — when there is no
 * `chunkId`. A fallback like `` `${sourceId ?? 'unknown'}-${rank}` `` collides
 * two ways: against a real `chunkId` that happens to read `unknown-1`, and,
 * worse, between two genuinely different unidentified chunks that share a rank.
 * The second one silently *hides evidence*, which is the opposite of what this
 * feature is for. No identity means "do not deduplicate".
 */
export const chunkIdentity = (chunk: RetrievedChunk): string | null =>
	chunk.chunkId ? `${chunk.collection ?? 'unknown-collection'}::${chunk.chunkId}` : null;

/**
 * Drops repeats from aggregated evidence, keeping the **best-ranked**
 * occurrence and returning everything in rank order.
 *
 * `ToolCallCard` concatenates a search's own chunks with those of its child
 * domain searches, and two domains can legitimately return the same chunk —
 * usually at different ranks. Keeping whichever came first in the array meant
 * keeping the parent's copy regardless of rank, which contradicted what this
 * function claimed to do.
 *
 * Chunks with no identity are never dropped: with nothing to compare them by,
 * removing one would be guessing, and guessing wrong deletes evidence.
 */
export const dedupeChunks = (chunks: RetrievedChunk[]): RetrievedChunk[] => {
	const { byIdentity, unidentified } = chunks.reduce<{
		byIdentity: Map<string, RetrievedChunk>;
		unidentified: RetrievedChunk[];
	}>(
		(acc, chunk) => {
			const identity = chunkIdentity(chunk);

			// Both branches mutate the accumulator, rather than one mutating and
			// the other spreading: the Map was already being mutated, and
			// rebuilding the array each pass made this quadratic as well as
			// inconsistent with itself.
			if (!identity) {
				acc.unidentified.push(chunk);

				return acc;
			}

			const previous = acc.byIdentity.get(identity);

			if (!previous || chunk.rank < previous.rank) acc.byIdentity.set(identity, chunk);

			return acc;
		},
		{ byIdentity: new Map(), unidentified: [] },
	);

	return [...byIdentity.values(), ...unidentified].sort((a, b) => a.rank - b.rank);
};

/**
 * Pairs each chunk with a React key that is unique within the list.
 *
 * Needed because two unidentified chunks can legitimately survive `dedupeChunks`
 * sharing every field, and because the array index is banned as a key here
 * (`react/no-array-index-key`) — rightly: a positional key is exactly what made
 * the old event correlation unreliable.
 *
 * The suffix only appears on actual repeats, so keys stay stable and readable
 * for the overwhelmingly common case.
 */
export const withKeys = (chunks: RetrievedChunk[]): { key: string; chunk: RetrievedChunk }[] => {
	const seen = new Map<string, number>();

	return chunks.map((chunk) => {
		const base =
			chunkIdentity(chunk) ?? `${chunk.sourceId ?? 'no-source'}::${chunk.fileName ?? 'no-file'}::${chunk.rank}`;
		const occurrence = (seen.get(base) ?? 0) + 1;

		seen.set(base, occurrence);

		return { key: occurrence === 1 ? base : `${base}#${occurrence}`, chunk };
	});
};
